'use client';

/**
 * AI Hub API client (SPEC-174.1 §2.2).
 *
 * Frontend methods for the AI Hub endpoints:
 *   POST /api/ai/chat         — chat completion (10/hr/user)
 *   GET  /api/ai/assistants   — list available assistants
 *   POST /api/ai/image         — image generation (Sprint 89)
 *   GET  /api/ai/history       — conversation history (Sprint 90)
 *
 * Spec ref: docs/SPEC-174-1-PROFILEIA-AI-HUB.md §2.2, §6.2.
 *
 * Anti-P-023: returns real typed values (no claim PASS).
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` statements.
 */

import { apiFetch } from './api-client';
import type {
  AIAssistantId,
  AILanguage,
  AIChatMessage,
  AIChatResponse,
  AIAssistantMeta,
} from '@profileia/shared';

// ─── POST /api/ai/chat ────────────────────────────────────────────────────

export interface SendChatParams {
  assistantId: AIAssistantId;
  message: string;
  language?: AILanguage;
  pageContext?: {
    path: string;
    section?: string;
    data?: Record<string, unknown>;
  };
  history?: AIChatMessage[];
  // v136: conversationId para persistencia de memoria
  conversationId?: string;
  idempotencyKey?: string;
}

/**
 * Send a chat message to an AI assistant.
 *
 * Rate limited: 10/hr/user (20/hr for soporte). Returns 429 if exceeded.
 * Returns 503 if the AI Hub is disabled or the specific assistant is off.
 */
export async function sendChatMessage(
  params: SendChatParams,
  signal?: AbortSignal,
): Promise<AIChatResponse> {
  return apiFetch<AIChatResponse>('/ai/chat', {
    method: 'POST',
    body: JSON.stringify(params),
    signal,
    // Sprint 90 fix: AI chat needs a longer timeout than the default
    // 15s WRITE_TIMEOUT_MS. The LLM (glm-4.5-flash) can take 5-15s to
    // respond, and web-search adds another 3-5s. 60s gives enough
    // headroom for the worst case (LLM + web-search + knowledge base).
    timeoutMs: 60_000, // 60 seconds
  });
}

// ─── POST /api/ai/chat/public (SPEC-177 Task A.4 — Sprint 91, amended Sprint 93) ─
//
// Public AI chat endpoint for anonymous landing visitors. Sprint 91
// originally allowed ONLY the `faq-negocio` assistant. Sprint 93 amendment
// (Founder directive) extends it to also allow `compensacion` — the
// `/plan-de-compensacion` page is a public conversion funnel and the
// assistant's knowledge base IS the verified spec. The widget uses this
// method when the user is NOT authenticated AND the assistantId is one of
// the two public assistants (Decision Co-Founder QA #1, amended Sprint 93).
//
// Auth: NONE (public — no JWT). Uses `auth: false` so `apiFetch` skips the
// access-token header. Rate limited 5/hr/IP server-side via
// `aiPublicChatLimiter`.

/**
 * Public assistants that anonymous (non-authenticated) users can chat with.
 * Sprint 93 amendment: `compensacion` added per Founder directive.
 */
export const PUBLIC_ASSISTANT_IDS = ['faq-negocio', 'compensacion'] as const;
export type PublicAssistantId = (typeof PUBLIC_ASSISTANT_IDS)[number];

/**
 * Returns true if the given assistantId can be used by anonymous users
 * via the public `/api/ai/chat/public` endpoint.
 */
export function isPublicAssistant(assistantId: string): assistantId is PublicAssistantId {
  return (PUBLIC_ASSISTANT_IDS as readonly string[]).includes(assistantId);
}

export interface SendPublicChatParams {
  assistantId: PublicAssistantId;
  message: string;
  language?: AILanguage;
  pageContext?: { path: string; section?: string };
  history?: AIChatMessage[];
  idempotencyKey?: string;
}

/**
 * Send a chat message to a public assistant (anonymous).
 *
 * Sprint 93 amendment: now accepts BOTH `faq-negocio` AND `compensacion`
 * (the caller passes the `assistantId` explicitly; the server-side Zod
 * schema enforces the whitelist).
 *
 * Rate limited: 5/hr/IP (more restrictive than the authed 10/hr/user).
 * Returns 429 (AI_RATE_LIMITED) if exceeded — the widget shows a CTA
 * "Inicia sesión para más mensajes" in that case.
 * Returns 503 if the AI Hub is disabled or the assistant is off.
 */
export async function sendPublicChatMessage(
  params: SendPublicChatParams,
  signal?: AbortSignal,
): Promise<AIChatResponse> {
  return apiFetch<AIChatResponse>('/ai/chat/public', {
    method: 'POST',
    body: JSON.stringify(params),
    auth: false,
    signal,
    // Same 60s timeout rationale as sendChatMessage — the LLM can take
    // 5-15s + web-search 3-5s.
    timeoutMs: 60_000,
  });
}

// ─── GET /api/ai/assistants ──────────────────────────────────────────────

/**
 * List all available AI assistants + their metadata (isAvailable reflects
 * feature flags — disabled assistants return isAvailable: false).
 */
export async function getAssistants(
  signal?: AbortSignal,
): Promise<AIAssistantMeta[]> {
  const res = await apiFetch<{ data: AIAssistantMeta[] }>('/ai/assistants', {
    signal,
    rawResponse: true,
  });
  return res.data ?? [];
}

// ─── GET /api/ai/history ──────────────────────────────────────────────────

/**
 * Response shape for GET /api/ai/history.
 * QA Co-Founder fix (ISSUE #3): replaces `any` with a proper typed
 * interface (anti-P-041 compliance — the file declares 0 `as any` casts).
 */
export interface AIHistoryResponse {
  conversations: Array<{
    id: string;
    assistantId: string;
    messages: AIChatMessage[];
    createdAt: string;
    updatedAt: string;
  }>;
  total: number;
  page: number;
  limit: number;
}

/**
 * Get the user's conversation history (paginated).
 * Sprint 90: returns all active conversations across all assistants.
 */
export async function getHistory(
  page: number = 1,
  limit: number = 20,
  signal?: AbortSignal,
): Promise<AIHistoryResponse> {
  const qs = `?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`;
  const res = await apiFetch<{ data: AIHistoryResponse }>(`/ai/history${qs}`, {
    signal,
    rawResponse: true,
  });
  return res.data;
}

// ─── Convenience: check if hub is enabled ─────────────────────────────────

/**
 * Check if the AI Hub is enabled (at least one assistant is available).
 * Returns false if the hub is disabled or all assistants are off.
 */
export async function isAIHubEnabled(
  signal?: AbortSignal,
): Promise<boolean> {
  try {
    const assistants = await getAssistants(signal);
    return assistants.some((a) => a.isAvailable);
  } catch {
    return false;
  }
}

// ─── POST /api/ai/image ──────────────────────────────────────────────────

/**
 * Generate an image from a text prompt.
 * Sprint 91: Uses Pollinations.ai (free, no API key).
 * Returns a base64 data URL that can be used directly in <img src="data:...">.
 *
 * Rate limited: 5/hr/user. Returns 429 if exceeded.
 */
export async function generateImage(
  prompt: string,
  size: '1024x1024' | '768x1344' | '1344x768' = '1024x1024',
  signal?: AbortSignal,
): Promise<{ imageUrl: string; prompt: string; createdAt: string }> {
  return apiFetch<{ imageUrl: string; prompt: string; createdAt: string }>('/ai/image', {
    method: 'POST',
    body: JSON.stringify({ prompt, size }),
    signal,
    timeoutMs: 90_000, // 90s — image generation can take up to 60s
  });
}

// ─── SPEC-185: AI Pro Army API client ────────────────────────────────────

// Re-export types so consumers can import them from '@/lib/api-ai'
// (fixes build error: Module declares 'AIProAgentMeta' locally, but it is not exported)
export type {
  AIProAgentId,
  AIProAgentMeta,
  AIProChatRequest,
  AIProChatResponse,
  AgentWorklog,
} from '@profileia/shared';

import type {
  AIProAgentId,
  AIProAgentMeta,
  AIProChatRequest,
  AIProChatResponse,
  AgentWorklog,
} from '@profileia/shared';

/**
 * Get list of PRO agents.
 * GET /api/ai/pro/agents
 */
export async function getProAgents(): Promise<AIProAgentMeta[]> {
  return apiFetch<AIProAgentMeta[]>('/ai/pro/agents', { method: 'GET' });
}

/**
 * Send a chat message to a PRO agent.
 * POST /api/ai/pro/chat — debits créditos PM atomically.
 */
export async function sendProChatMessage(
  params: Omit<AIProChatRequest, 'agentId'> & { agentId: AIProAgentId },
  signal?: AbortSignal,
): Promise<AIProChatResponse> {
  return apiFetch<AIProChatResponse>('/ai/pro/chat', {
    method: 'POST',
    body: JSON.stringify(params),
    signal,
    timeoutMs: 120_000, // 120s — PRO agents can take longer (web-search, RAG)
  });
}

/**
 * Get the user's worklog for a PRO agent.
 * GET /api/ai/pro/worklog/:agentId
 */
export async function getProWorklog(
  agentId: AIProAgentId,
): Promise<AgentWorklog> {
  return apiFetch<AgentWorklog>(`/ai/pro/worklog/${agentId}`, { method: 'GET' });
}

/**
 * Delegate a task from one agent to another.
 * POST /api/ai/pro/delegate
 */
export async function delegateTask(params: {
  fromAgentId: AIProAgentId;
  toAgentId: AIProAgentId;
  task: string;
}): Promise<{ delegated: boolean; fromAgent: string; toAgent: string; message: string }> {
  return apiFetch<{ delegated: boolean; fromAgent: string; toAgent: string; message: string }>(
    '/ai/pro/delegate',
    { method: 'POST', body: JSON.stringify(params) },
  );
}

/**
 * Upload a document to the workspace + process for RAG.
 * POST /api/ai/pro/workspace/documents
 */
export async function uploadDocument(params: {
  file: File;
}): Promise<{
  docId: string;
  filename: string;
  fileType: string;
  sizeBytes: number;
  chunkCount: number;
  success: boolean;
  error?: string;
}> {
  // Read file as base64
  const reader = new FileReader();
  const base64 = await new Promise<string>((resolve, reject) => {
    reader.onload = () => {
      const result = reader.result as string;
      // Strip data URL prefix (data:application/pdf;base64,...)
      const base64Data = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(params.file);
  });

  // Determine file type
  const ext = params.file.name.split('.').pop()?.toLowerCase() ?? '';
  const fileType = ext === 'pdf' ? 'pdf'
    : ext === 'docx' || ext === 'doc' ? 'docx'
    : ext === 'xlsx' || ext === 'xls' ? 'xlsx'
    : ext === 'txt' || ext === 'md' ? 'txt'
    : 'img';

  return apiFetch('/ai/pro/workspace/documents', {
    method: 'POST',
    body: JSON.stringify({
      fileContent: base64,
      filename: params.file.name,
      fileType,
    }),
    timeoutMs: 120_000, // RAG processing can take time
  });
}

/**
 * List documents in the workspace.
 * GET /api/ai/pro/workspace/documents
 */
export async function listDocuments(): Promise<{
  documents: Array<{
    docId: string;
    filename: string;
    fileType: string;
    sizeBytes: number;
    uploadedAt: string;
    processedRAG: boolean;
    chunkCount: number;
  }>;
  storageUsedBytes: number;
  storageLimitBytes: number;
}> {
  return apiFetch('/ai/pro/workspace/documents', { method: 'GET' });
}

/**
 * Delete a document.
 * DELETE /api/ai/pro/workspace/documents/:id
 */
export async function deleteDocument(docId: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/ai/pro/workspace/documents/${docId}`, { method: 'DELETE' });
}

/**
 * Generate image PRO (CogView-4).
 * POST /api/ai/pro/image
 */
export async function generateProImage(
  prompt: string,
  size?: '1024x1024' | '768x1344' | '864x1152' | '1344x768' | '1152x864' | '1440x720' | '720x1440',
  signal?: AbortSignal,
): Promise<{ base64: string; dataUrl: string; cost: string; model: string }> {
  return apiFetch('/ai/pro/image', {
    method: 'POST',
    body: JSON.stringify({ prompt, size }),
    signal,
    timeoutMs: 90_000,
  });
}
